import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  INSURANCE_STATUS_LABELS,
  listInsuranceClaims,
  type InsuranceClaim,
  type InsuranceClaimStatus,
} from '@/lib/api/insurance.js';
import { formatCurrency, formatDateTime, prettyLabel } from '@/lib/format.js';

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-bold text-slate-950">{value}</p>
    </div>
  );
}

function computeStats(claims: InsuranceClaim[]) {
  const open = claims.filter(
    (c) => !['payment_received', 'denied', 'cancelled'].includes(c.status),
  );
  const paid = claims.filter((c) => c.status === 'payment_received');
  const totalPayout = paid.reduce((sum, c) => sum + Number(c.shopPayout ?? 0), 0);
  const pendingPayout = open.reduce((sum, c) => sum + Number(c.shopPayout ?? c.approvedAmount ?? 0), 0);
  const denied = claims.filter((c) => c.status === 'denied').length;

  return {
    total: claims.length,
    open: open.length,
    paid: paid.length,
    denied,
    totalPayout,
    pendingPayout,
  };
}

export function InsuranceReportPage() {
  const claimsQuery = useQuery({
    queryKey: ['insurance-claims-report'],
    queryFn: () => listInsuranceClaims({ pageSize: 100 }),
  });

  const claims = claimsQuery.data?.data ?? [];
  const stats = useMemo(() => computeStats(claims), [claims]);

  if (claimsQuery.isLoading) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading insurance claims…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-950">Insurance Claims</h1>
        <p className="mt-1 text-sm text-slate-600">Safeware and third-party repair insurance tracking.</p>
      </div>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard label="Total claims" value={stats.total} />
        <StatCard label="Open claims" value={stats.open} />
        <StatCard label="Paid out" value={formatCurrency(stats.totalPayout)} />
        <StatCard label="Pending payout (est.)" value={formatCurrency(stats.pendingPayout)} />
        <StatCard label="Completed (paid)" value={stats.paid} />
        <StatCard label="Denied" value={stats.denied} />
      </section>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Provider</th>
              <th className="px-5 py-3">Claim #</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Payout</th>
              <th className="px-5 py-3">Created</th>
              <th className="px-5 py-3">Ticket</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {claims.map((claim) => (
              <tr key={claim.id} className="hover:bg-slate-50">
                <td className="px-5 py-4 font-medium text-slate-950">{claim.insuranceProvider}</td>
                <td className="px-5 py-4 text-slate-600">{claim.claimNumber ?? '—'}</td>
                <td className="px-5 py-4">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                    {INSURANCE_STATUS_LABELS[claim.status as InsuranceClaimStatus] ?? prettyLabel(claim.status)}
                  </span>
                </td>
                <td className="px-5 py-4 font-semibold text-slate-950">
                  {claim.shopPayout ? formatCurrency(claim.shopPayout) : claim.approvedAmount ? formatCurrency(claim.approvedAmount) : '—'}
                </td>
                <td className="px-5 py-4 text-slate-600">{formatDateTime(claim.createdAt)}</td>
                <td className="px-5 py-4">
                  <Link to={`/tickets/${claim.ticketId}`} className="font-semibold text-primary hover:underline">
                    View ticket
                  </Link>
                </td>
              </tr>
            ))}
            {claims.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-slate-500">
                  No insurance claims yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
